import dayjs, { Dayjs } from "dayjs";
import { AllOrdersFilters, OrderRow, OrderStatus, OrderType } from "./types";
import { OrdersResponse } from "../../../api/orders/orderInterfaces";
import { LineStatus, OrdersByLineItemFilter } from "../OrdersByLineItem/types";

export const createRowsOfOrder = (order: OrdersResponse): OrderRow[] => {
  const parent: OrderRow = {
    id: `${order.accountId}-${order.orderNumber}`,
    accountId: order.accountId,
    path: [order.orderNumber],
    promotion: order.promotionId,
    investigation: order.investigations,
    orderNumber: order.orderNumber,
    generatedFrom: order.generatedFrom,
    client: order.client,
    dealerReference: order.dealerReference,
    submittedBy: order.submittedBy
      ? order.submittedBy.toUpperCase()
      : order.submittedBy,
    orderType: order.orderType,
    orderStatus: order.orderStatus,
    dateTimeSubmitted: order.submittedAt,
    dateTimeCreated: order.createdAt,
    isCreditCheckNotOk: order.isCreditCheckNotOk,
  };

  return [parent];
};

export type OrderRowSortField = keyof (Omit<
  OrderRow,
  "id" | "path" | "isCreditCheckNotOk" | "client"
> & {
  // eslint-disable-next-line camelcase
  __tree_data_group__: string;
});

const SORT_FIELD_MAP: Record<OrderRowSortField, string> = {
  __tree_data_group__: "ORDER_NUMBER",
  orderNumber: "ORDER_NUMBER",
  generatedFrom: "EXT_ORDER_NUMBER",
  dealerReference: "DEALER_REFERENCE",
  submittedBy: "SUBMITTED_BY",
  orderStatus: "ORDER_STATUS",
  orderType: "ORDER_TYPE",
  promotion: "PROMOTION_ID",
  investigation: "INVESTIGATION",
  dateTimeSubmitted: "SUBMITTED_AT",
  dateTimeCreated: "CREATED_AT",
  accountId: "ACCOUNT_ID",
};

export const getSortField = (
  fieldFromTable: OrderRowSortField | null,
  isAllAccounts: boolean
): string | undefined => {
  if (isAllAccounts && fieldFromTable === "__tree_data_group__") {
    return SORT_FIELD_MAP.accountId;
  }
  return fieldFromTable ? SORT_FIELD_MAP[fieldFromTable] : "UPDATED_AT";
};

const FILTER_COMPARISON_KEYS: (keyof AllOrdersFilters)[] = [
  "orderTypes",
  "orderStatuses",
  "orderNumber",
  "dealerReference",
  "promotion",
  "orderCreationDateFrom",
  "orderCreationDateTo",
  "orderSubmittedDateFrom",
  "orderSubmittedDateTo",
];

const areArraysEqual = (first?: string[], second?: string[]): boolean => {
  if (!first && !second) return true;
  if (!first || !second) return false;
  if (first.length !== second.length) return false;

  const sortedFirst = [...first].sort();
  const sortedSecond = [...second].sort();

  return sortedFirst.every((value, index) => value === sortedSecond[index]);
};

const areDayjsValuesEqual = (first?: Dayjs, second?: Dayjs): boolean => {
  if (!first && !second) return true;
  if (!first || !second) return false;
  return first.isSame(second, "day");
};

const areDateValuesEqual = (first?: Date, second?: Date): boolean => {
  if (!first && !second) return true;
  if (!first || !second) return false;
  return first.getTime() === second.getTime();
};

const areStringValuesEqual = (first?: string, second?: string): boolean => {
  if (!first && !second) return true;
  if (!first || !second) return false;
  return first === second;
};

export const getDefaultFilters = (
  accountId: string,
  enableOptimization: boolean,
  tabIndex: number,
  enableTabFeature: boolean,
  currentDate: Dayjs = dayjs()
): AllOrdersFilters => {
  let orderStatuses: OrderStatus[];

  if (!enableTabFeature) {
    orderStatuses = [...Object.values(OrderStatus)];
  } else if (tabIndex === 1) {
    orderStatuses = [OrderStatus.UNSUBMITTED];
  } else {
    orderStatuses = Object.values(OrderStatus).filter(
      (status) => status !== OrderStatus.UNSUBMITTED
    );
  }

  const filters: AllOrdersFilters = {
    ...(accountId ? { accountIds: [accountId] } : {}),
    orderTypes: [...Object.values(OrderType)],
    orderStatuses,
  };

  if (enableOptimization) {
    const endDate = currentDate.clone();
    filters.orderCreationDateTo = endDate;
    filters.orderCreationDateFrom = endDate.clone().subtract(3, "months");
  }

  return filters;
};

export const getFiltersForOrderByLineItems = (
  accountId: string[],
  existingFilters: OrdersByLineItemFilter,
  tabIndex: number,
  zeroTabLineStatusFilter?: LineStatus[],
  currentDate: Dayjs = dayjs()
): OrdersByLineItemFilter => {
  let lineStatuses: LineStatus[] = [];
  const endDate = currentDate.clone();

  if (tabIndex === 0) {
    lineStatuses = zeroTabLineStatusFilter ?? Object.values(LineStatus);
  } else if (tabIndex === 1) {
    lineStatuses = [LineStatus.BACK_ORDERED];
  } else if (tabIndex === 2) {
    lineStatuses = [LineStatus.ON_HOLD];
  } else if (tabIndex === 3) {
    lineStatuses = [
      LineStatus.DEERE_INVESTIGATION,
      LineStatus.DEALER_INVESTIGATION,
    ];
  } else if (tabIndex === 4) {
    lineStatuses = [LineStatus.SENT_TO_SUPPLIER];
  }

  // Merge existingFilters, but override lineStatus and date range
  return {
    ...(accountId ? { accountIds: accountId } : {}),
    ...existingFilters,
    lineStatus: lineStatuses,
    latestOrderCreationDateTo: endDate,
    latestOrderCreationDateFrom: endDate.clone().subtract(3, "months"),
  };
};

export const sanitizeFilters = (
  filters?: AllOrdersFilters | null
): AllOrdersFilters => {
  if (!filters) return {};

  const sanitized: AllOrdersFilters = {};

  if (filters.orderTypes) sanitized.orderTypes = [...filters.orderTypes];
  if (filters.orderStatuses)
    sanitized.orderStatuses = [...filters.orderStatuses];
  if (filters.orderNumber !== undefined)
    sanitized.orderNumber = filters.orderNumber;
  if (filters.dealerReference !== undefined)
    sanitized.dealerReference = filters.dealerReference;
  if (filters.promotion !== undefined) sanitized.promotion = filters.promotion;
  if (filters.orderCreationDateFrom)
    sanitized.orderCreationDateFrom = filters.orderCreationDateFrom.clone();
  if (filters.orderCreationDateTo)
    sanitized.orderCreationDateTo = filters.orderCreationDateTo.clone();
  if (filters.orderSubmittedDateFrom)
    sanitized.orderSubmittedDateFrom = new Date(
      filters.orderSubmittedDateFrom.getTime()
    );
  if (filters.orderSubmittedDateTo)
    sanitized.orderSubmittedDateTo = new Date(
      filters.orderSubmittedDateTo.getTime()
    );

  return sanitized;
};

export const applyFilterOverrides = (
  base: AllOrdersFilters,
  overrides: AllOrdersFilters
): AllOrdersFilters => {
  const merged: AllOrdersFilters = {
    ...base,
    ...overrides,
  };

  if (overrides.orderTypes) merged.orderTypes = [...overrides.orderTypes];
  if (overrides.orderStatuses)
    merged.orderStatuses = [...overrides.orderStatuses];
  if (overrides.orderCreationDateFrom)
    merged.orderCreationDateFrom = overrides.orderCreationDateFrom.clone();
  if (overrides.orderCreationDateTo)
    merged.orderCreationDateTo = overrides.orderCreationDateTo.clone();
  if (overrides.orderSubmittedDateFrom)
    merged.orderSubmittedDateFrom = new Date(
      overrides.orderSubmittedDateFrom.getTime()
    );
  if (overrides.orderSubmittedDateTo)
    merged.orderSubmittedDateTo = new Date(
      overrides.orderSubmittedDateTo.getTime()
    );

  return merged;
};

export const checkIfFilterApplied = (
  filters: AllOrdersFilters,
  defaultFilters: AllOrdersFilters
): boolean =>
  FILTER_COMPARISON_KEYS.some((key) => {
    switch (key) {
      case "orderTypes":
        return !areArraysEqual(filters.orderTypes, defaultFilters.orderTypes);
      case "orderStatuses":
        return !areArraysEqual(
          filters.orderStatuses,
          defaultFilters.orderStatuses
        );
      case "orderCreationDateFrom":
        return !areDayjsValuesEqual(
          filters.orderCreationDateFrom,
          defaultFilters.orderCreationDateFrom
        );
      case "orderCreationDateTo":
        return !areDayjsValuesEqual(
          filters.orderCreationDateTo,
          defaultFilters.orderCreationDateTo
        );
      case "orderSubmittedDateFrom":
        return !areDateValuesEqual(
          filters.orderSubmittedDateFrom,
          defaultFilters.orderSubmittedDateFrom
        );
      case "orderSubmittedDateTo":
        return !areDateValuesEqual(
          filters.orderSubmittedDateTo,
          defaultFilters.orderSubmittedDateTo
        );
      case "orderNumber":
        return !areStringValuesEqual(
          filters.orderNumber,
          defaultFilters.orderNumber
        );
      case "dealerReference":
        return !areStringValuesEqual(
          filters.dealerReference,
          defaultFilters.dealerReference
        );
      case "promotion":
        return !areStringValuesEqual(
          filters.promotion,
          defaultFilters.promotion
        );
      default:
        return false;
    }
  });

export const shouldShowNoDataAvailableError = (
  orderLinesLength: number,
  isFilterApplied: boolean,
  showTechnicalError: boolean,
  showAuthenticationError: boolean
) =>
  !orderLinesLength &&
  !isFilterApplied &&
  !showTechnicalError &&
  !showAuthenticationError;

export const shouldShowNoDataAvailableForFilterError = (
  orderLinesLength: number,
  isFilterApplied: boolean,
  showTechnicalError: boolean,
  showAuthenticationError: boolean
) =>
  !orderLinesLength &&
  isFilterApplied &&
  !showTechnicalError &&
  !showAuthenticationError;
